import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getRouteApi } from "@tanstack/react-router";
import { DateTime } from "luxon";
// ui
import { DateRangePicker } from "@components/date-range-picker/date-range-picker";
import { Box, Divider, Group, Text, SegmentedControl } from "@mantine/core";
// types
import type { TimeGranularity } from "@utils/chart-data-conversions/timelines";

const route = getRouteApi("/_authenticated/app/players/$playerId/insights");

const MAX_NUM_COLUMNS = 180;

const willGranularityResultInFewEnoughColumns = (
  start: DateTime,
  end: DateTime,
  granularity: TimeGranularity,
): boolean => {
  return end.diff(start, granularity)[`${granularity}s`] <= MAX_NUM_COLUMNS;
};

export const DateRangeSelect: React.FC = () => {
  const { t } = useTranslation();
  const navigate = route.useNavigate();

  const {
    granularity,
    startDate: startDateStr,
    endDate: endDateStr,
  } = route.useSearch();

  const startDate = DateTime.fromISO(startDateStr);
  const endDate = DateTime.fromISO(endDateStr);

  const granularityOpts: TimeGranularity[] = useMemo(() => {
    const ret: TimeGranularity[] = [];

    const allOpts: TimeGranularity[] = ["day", "week", "month"];
    allOpts.forEach((opt) => {
      if (willGranularityResultInFewEnoughColumns(startDate, endDate, opt)) {
        ret.push(opt);
      }
    });

    return ret;
  }, [endDate, startDate]);

  return (
    <Group>
      <Box>
        <Text>{t("playerInsights.configureSection.dateRange")}</Text>
        <DateRangePicker />
      </Box>

      <Divider orientation="vertical" />

      <Box>
        <Text>{t("playerInsights.configureSection.granularity")}</Text>
        <SegmentedControl
          data={granularityOpts}
          value={granularity}
          onChange={async (val) => {
            await navigate({
              search: (current) => ({
                ...current,
                granularity: val,
              }),
            });
          }}
        />
      </Box>
    </Group>
  );
};
